import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Header.css';
import { useStore } from '../../store/useStore';
import { TonConnectButton } from '@tonconnect/ui-react';
import SettingsIcon from '../../assets/icons/settings.svg';
import { useSound } from '../../hooks/useSound';
import NotificationBell from '../NotificationBell/NotificationBell';
import SoundManager from '../../services/SoundManager';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    tonPrice,
    spermPrice,
    user,
    graphicsQuality,
    setGraphicsQuality,
    language,
    setLanguage,
    fetchPrices,
    soundEnabled,
    setSoundEnabled,
    musicEnabled,
    setMusicEnabled,
    selectedMusic,
    setSelectedMusic
  } = useStore();

  // Звуки
  const playButtonClick = useSound('ui/ui_button_click.mp3');

  const [showSettings, setShowSettings] = useState(false);
  const [backgroundMusic, setBackgroundMusic] = useState<HTMLAudioElement | null>(null);

  // Инициализация музыки
  useEffect(() => {
    const audio = document.getElementById('main-menu-music') as HTMLAudioElement;
    if (audio) {
      setBackgroundMusic(audio);
      audio.volume = 0.3; // Громкость 30%

      // Используем настройку из store (уже загружена из БД)
      console.log('🎵 Инициализация музыки, musicEnabled:', musicEnabled);
      if (musicEnabled) {
        // Попытка автовоспроизведения
        audio.play().catch(err => {
          console.log('Автовоспроизведение заблокировано:', err);
        });
      }
    }
  }, [musicEnabled]);

  useEffect(() => {
    // Загружаем курсы сразу при монтировании
    fetchPrices();

    // Обновляем курсы каждые 3 минуты
    const interval = setInterval(() => {
      fetchPrices();
    }, 180000); // 3 минуты

    return () => clearInterval(interval);
  }, [fetchPrices]);

  const handleQualityChange = (quality: 'low' | 'high') => {
    playButtonClick();
    setGraphicsQuality(quality);
    // Сохраняем в localStorage
    localStorage.setItem('graphicsQuality', quality);
  };

  const handleLanguageChange = (lang: 'ru' | 'en') => {
    playButtonClick();
    setLanguage(lang);
    // Сохраняем в localStorage
    localStorage.setItem('language', lang);
  };

  const handleMusicToggle = () => {
    if (!backgroundMusic) return;

    playButtonClick();
    const newState = !musicEnabled;
    setMusicEnabled(newState);

    if (newState) {
      backgroundMusic.play().catch(err => {
        console.error('Ошибка воспроизведения музыки:', err);
      });
    } else {
      backgroundMusic.pause();
    }
  };

  const handleSoundToggle = () => {
    playButtonClick();
    const newState = !soundEnabled;
    setSoundEnabled(newState);
  };

  const handleTrackChange = (trackKey: string) => {
    playButtonClick();
    setSelectedMusic(trackKey);
    SoundManager.switchBackgroundMusic(trackKey);
  };

  return (
    <header className="header">
      <div className="header-content">
        {/* Левая часть - курсы валют */}
        <div className="header-rates">
          <div className="rate-item">
            <span className="rate-label">💎 TON</span>
            <span className="rate-value">${tonPrice.toFixed(2)}</span>
          </div>
          <div className="rate-item">
            <span className="rate-label">🌙 SPERM</span>
            <span className="rate-value">${spermPrice.toFixed(3)}</span>
          </div>
        </div>

        {/* Правая часть - колокольчик и настройки */}
        <div className="header-settings">
          <NotificationBell />

          <button
            className="settings-btn"
            onClick={() => { playButtonClick(); setShowSettings(!showSettings); }}
            aria-label={t('header.settings')}
          >
            <img src={SettingsIcon} alt={t('header.settings')} />
          </button>

          {/* Dropdown прямо здесь - будет позиционироваться абсолютно */}
          {showSettings && (
            <div className="settings-dropdown">
                <div className="settings-section">
                  <div className="settings-title">{t('header.graphics')}</div>
                  <div className="quality-options">
                    <button
                      className={`quality-btn ${graphicsQuality === 'low' ? 'active' : ''}`}
                      onClick={() => handleQualityChange('low')}
                    >
                      <span className="quality-icon">📉</span>
                      <span className="quality-text">
                        <span className="quality-name">{t('header.low')}</span>
                        <span className="quality-desc">{t('header.battery')}</span>
                      </span>
                    </button>
                    <button
                      className={`quality-btn ${graphicsQuality === 'high' ? 'active' : ''}`}
                      onClick={() => handleQualityChange('high')}
                    >
                      <span className="quality-icon">📈</span>
                      <span className="quality-text">
                        <span className="quality-name">{t('header.high')}</span>
                        <span className="quality-desc">{t('header.quality')}</span>
                      </span>
                    </button>
                  </div>
                </div>

                <div className="settings-divider"></div>

                <div className="settings-section">
                  <div className="settings-title">{t('header.music')}</div>
                  <div className="music-options">
                    {/* Фоновая музыка с выбором трека */}
                    <div className="music-row">
                      <button
                        className={`music-toggle-btn ${musicEnabled ? 'active' : ''}`}
                        onClick={handleMusicToggle}
                      >
                        <span className="music-icon">{musicEnabled ? '🎵' : '🔇'}</span>
                        <span className="music-text">
                          <span className="music-name">{t('header.music')}</span>
                          <span className="music-desc">{musicEnabled ? '✓' : '✗'}</span>
                        </span>
                      </button>
                      <select
                        value={selectedMusic}
                        onChange={(e) => handleTrackChange(e.target.value)}
                        className="track-select-dropdown"
                        disabled={!musicEnabled}
                      >
                        {SoundManager.getAvailableTracks().map(track => (
                          <option key={track.key} value={track.key}>
                            {track.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Звуковые эффекты */}
                    <button
                      className={`music-toggle-btn ${soundEnabled ? 'active' : ''}`}
                      onClick={handleSoundToggle}
                    >
                      <span className="music-icon">{soundEnabled ? '🔊' : '🔇'}</span>
                      <span className="music-text">
                        <span className="music-name">{t('header.sounds')}</span>
                        <span className="music-desc">{soundEnabled ? '✓' : '✗'}</span>
                      </span>
                    </button>
                  </div>
                </div>

                <div className="settings-divider"></div>

                {/* TON Wallet секция с максимальным z-index */}
                <div className="settings-section">
                  <div className="settings-title">TON Wallet</div>
                  <div className="ton-connect-settings" style={{ position: 'relative', zIndex: 999999 }}>
                    <TonConnectButton className="ton-connect-btn-settings" />
                  </div>
                </div>

                <div className="settings-divider"></div>

                <div className="settings-section">
                  <div className="settings-title">{t('header.language')}</div>
                  <div className="language-options">
                    <button
                      className={`language-btn ${language === 'ru' ? 'active' : ''}`}
                      onClick={() => handleLanguageChange('ru')}
                    >
                      <span className="language-flag">🇷🇺</span>
                      <span className="language-name">{t('header.russian')}</span>
                    </button>
                    <button
                      className={`language-btn ${language === 'en' ? 'active' : ''}`}
                      onClick={() => handleLanguageChange('en')}
                    >
                      <span className="language-flag">🇬🇧</span>
                      <span className="language-name">{t('header.english')}</span>
                    </button>
                  </div>
                </div>

                {localStorage.getItem('webauth') === 'true' && (
                  <>
                    <div className="settings-divider"></div>
                    <div className="settings-section">
                      <button
                        className="logout-btn"
                        onClick={() => {
                          localStorage.removeItem('token');
                          localStorage.removeItem('webauth');
                          localStorage.removeItem('user');
                          localStorage.removeItem('user_avatar');
                          window.location.href = '/login.html';
                        }}
                      >
                        <span style={{marginRight:'8px'}}>🚪</span>
                        {t('header.logout', 'Выйти')}
                      </button>
                    </div>
                  </>
                )}
            </div>
          )}
        </div>
      </div>

      {/* Индикатор обновления курсов */}
      <div className="update-indicator">
        <div className="update-dot"></div>
      </div>

      {/* Overlay для закрытия settings - рендерится вне header */}
      {showSettings && (
        <div
          className="settings-overlay"
          onClick={() => { playButtonClick(); setShowSettings(false); }}
        />
      )}
    </header>
  );
};

export default Header;